import React, { useState } from "react";
import * as styles from "./ContrastChecker.module.scss";
import { FiRefreshCw } from "react-icons/fi";
import { PiEyedropperSampleThin } from "react-icons/pi";

interface ApiResponse {
    "foreground-color": string;
    "background-color": string;
    contrastRatio: number;
    wcag: {
        normalText: { AA: boolean; AAA: boolean };
        largeText: { AA: boolean; AAA: boolean };
    };
    compliance: { normalText: string; largeText: string };
    tip: string;
}

const ContrastChecker: React.FC = ({
    urlResponseText,
    requiredUrlMessage
}) => {
    const [foreground, setForeground] = useState("#2F27C5");
    const [background, setBackground] = useState("#C7C4D7");
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<ApiResponse | null>(null);
    const [error, setError] = useState("");


    const handleSwap = () => {
        setForeground(background);
        setBackground(foreground);
    };

    const handleCheck = async () => {
        setIsLoading(true);
        setError("");
        setData(null);


        try {
            const response = await fetch("/.netlify/functions/contrast-checker", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ foreground, background }),
            });

            if (!response.ok) throw new Error("API error");
            const json = await response.json();
            setData(json);
        } catch(err) {
            if(foreground==="" || background===""){
                setError(requiredUrlMessage)
            } else{
                const message = err?.message || "Something went wrong";
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    };


    let localeResponseText = {
        "AA": "Increase contrast to at least 4.5:1 for normal text (AA).",
        "AAA": "To reach AAA for normal text, target 7.0:1 by slightly darkening the background or lightening the text.",
        "Success": "Great! Your colors meet the required contrast."
    }

    return (
        <div className={`${styles.wrapper}`}>
            <div className={`container c-padding-bottom`}>
                <h1 className={`global-spacing-bottom c-padding-top `}>{urlResponseText.Title}</h1>
                <p className={`global-spacing-bottom ${styles.para}`}>{urlResponseText.Para}</p>
                <div className={styles.colorControl}>
                    <div className={`global-spacing-bottom ${styles.controls}`}>
                        <div className={styles.buttonControl}>
                            <div className={styles.colorGroup}>
                                <label>{urlResponseText.Foreground}</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        value={foreground}
                                        onChange={(e) => setForeground(e.target.value)}
                                    />
                                    <button
                                        className={styles.colorBtn}
                                        style={{ backgroundColor: foreground }}
                                    >
                                        <PiEyedropperSampleThin />
                                        <input
                                            type="color"
                                            value={foreground}
                                            onChange={(e) => setForeground(e.target.value)}
                                        />
                                    </button>
                                </div>
                            </div>
                            <div className={styles.colorGroup}>
                                <label>{urlResponseText.Background}</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        value={background}
                                        onChange={(e) => setBackground(e.target.value)}
                                    />
                                    <button
                                        className={styles.colorBtn}
                                        style={{ backgroundColor: background }}
                                    >
                                        <PiEyedropperSampleThin />
                                        <input
                                            type="color"
                                            value={background}
                                            onChange={(e) => setBackground(e.target.value)}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div>
                            <button className={styles.swapBtn} onClick={handleSwap}>
                                <FiRefreshCw />
                            </button>
                        </div>
                        <div>
                            <button
                                className={`primaryFilled`}
                                onClick={handleCheck}
                                disabled={isLoading}
                            >
                                {isLoading ? urlResponseText.CheckNow : urlResponseText.CheckNow}
                            </button>
                        </div>
                    </div>
                    {error && <p className={styles.errorText}>{error}</p>}
                    {data && (
                        <div className={styles.results}>
                            <div className={` ${styles.preview}`} style={{ color: data["foreground-color"], backgroundColor: data["background-color"], }} >
                                <h2 className={`h3 ${styles.lorem}`}>{urlResponseText.LoremIpsum}</h2>
                                <p> {urlResponseText.LoremIpsumDescription}</p>
                            </div>

                            <div className={styles.contrastRatio}>
                                <p className={`global-spacing-top`} >{urlResponseText.ContrastRatio}</p>
                                <h4 className={styles.contrastNumber}>
                                    <strong>{data.contrastRatio.toFixed(2)}:1</strong>
                                </h4>
                            </div>
                            <div className={`global-spacing-top ${styles.compliance}`}>
                                <p className={styles.textCompliance}>{urlResponseText.WCAGCompliance}</p>
                                <table className={styles.tableData}>
                                    <tbody>
                                        <tr>
                                            <td className={styles.tdHead}>{urlResponseText.NormalText}</td>
                                            <td>
                                                <div className={data.wcag.normalText.AA ? styles.pass : styles.fail}>
                                                    {data.wcag.normalText.AA ? urlResponseText.AAPass : urlResponseText.AAFail}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={data.wcag.normalText.AAA ? styles.pass : styles.fail}>
                                                    {data.wcag.normalText.AAA ? urlResponseText.AAAPass : urlResponseText.AAAFail}
                                                </div>

                                            </td>
                                        </tr>
                                        <tr>
                                            <td className={styles.tdHead}>{urlResponseText.LargeText}</td>
                                            <td >
                                                <div className={data.wcag.largeText.AA ? styles.pass : styles.fail}>
                                                    {data.wcag.largeText.AA ? urlResponseText.AAPass : urlResponseText.AAFail}
                                                </div>

                                            </td>
                                            <td >
                                                <div className={data.wcag.largeText.AAA ? styles.pass : styles.fail}>
                                                    {data.wcag.largeText.AAA ? urlResponseText.AAAPass : urlResponseText.AAAFail}
                                                </div>

                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className={styles.tip}>
                                <p>{urlResponseText.Suggestion}</p>

                                {data.tip === localeResponseText.AA && (
                                    <p>
                                        {urlResponseText.Tips.AA}
                                    </p>
                                )}

                                {data.tip === localeResponseText.AAA && (
                                    <p>
                                        {urlResponseText.Tips.AAA}
                                    </p>
                                )}

                                {data.tip === localeResponseText.Success && (
                                    <p>
                                        {urlResponseText.Tips.Success}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>

    );
};

export default ContrastChecker;
